import * as React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "../../../../../api/axios";
import useAuth from "../../../../../hooks/useAuth";
import { Button, CircularProgress } from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";

export default function ControlledAccordions() {
	const [expanded, setExpanded] = React.useState(false);
	const [orders, setOrders] = React.useState([]);
	const [error, setError] = React.useState(null);
	const [loading, setLoading] = React.useState(false);
	const [refetchTrigger, setRefetchTrigger] = React.useState(0); // trigger refetch

	const { auth, accountMode } = useAuth();
	const DEMO_ORDER_ENDPOINT = `/demo-trade-history/${auth?.id}`;
	const REAL_ORDER_ENDPOINT = `/real-trade-history/${auth?.id}`;

	const activeEndPoint =
		accountMode === "real" ? REAL_ORDER_ENDPOINT : DEMO_ORDER_ENDPOINT;

	const fetchOrders = React.useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await axios.get(activeEndPoint);
			if (response.status === 200 && response.data?.message) {
				setOrders(response.data.message);
			} else {
				setError("Failed to fetch orders");
			}
		} catch (err) {
			console.error("Fetch orders error:", err);
			setError(err.message || "Error fetching orders");
		} finally {
			setLoading(false);
		}
	}, [activeEndPoint]);

	// Fetch orders on mount and whenever refetchTrigger changes
	React.useEffect(() => {
		fetchOrders();
	}, [fetchOrders, refetchTrigger]);

	const handleChange = (panel) => (event, isExpanded) => {
		setExpanded(isExpanded ? panel : false);
	};

	const deleteSingleOrderHandler = async (tradeId, cryptoSymbol) => {
		const REAL_SINGLE_ORDER_DEL_ENDPOINT = `/delete-real-single-order/${auth?.id}/${cryptoSymbol}/${tradeId}`;
		const DEMO_SINGLE_ORDER_DEL_ENDPOINT = `/delete-demo-single-order/${auth?.id}/${cryptoSymbol}/${tradeId}`;
		const activeOrderDelEndpoint =
			accountMode === "real"
				? REAL_SINGLE_ORDER_DEL_ENDPOINT
				: DEMO_SINGLE_ORDER_DEL_ENDPOINT;
		try {
			const response = await axios.patch(activeOrderDelEndpoint);
			if (response.status === 200) {
				// Refetch after successful delete
				setRefetchTrigger((prev) => prev + 1);
			}
		} catch (error) {
			console.error("Delete single order error:", error);
		}
	};

	const deleteAllOrderHandler = async () => {
		const DELETE_ALL_DEMO_ORDER_ENDPOINT = `/delete-all-demo-orders/${auth?.id}`;
		const DELETE_ALL_REAL_ORDER_ENDPOINT = `/delete-all-real-orders/${auth?.id}`;
		const ativeDeleteAllEndpoint =
			accountMode === "real"
				? DELETE_ALL_REAL_ORDER_ENDPOINT
				: DELETE_ALL_DEMO_ORDER_ENDPOINT;

		try {
			const response = await axios.patch(ativeDeleteAllEndpoint);
			if (response.status === 200) {
				// Refetch after successful delete all
				setRefetchTrigger((prev) => prev + 1);
			}
		} catch (error) {
			console.error("Delete all orders error:", error);
		}
	};

	if (loading) {
		return <CircularProgress />;
	}

	if (error) {
		return <p>Unable to fetch data: {error}</p>;
	}

	if (!orders || orders.length === 0) {
		return <p>No orders found.</p>;
	}

	const filledOrders = orders.filter((d) => d.orderStatus === "filled");

	return (
		<div>
			<Button
				onClick={deleteAllOrderHandler}
				sx={{ bgcolor: "red", color: "white", my: "0.5rem" }}>
				Delete all <DeleteSweepIcon sx={{ ml: "0.5rem" }} />
			</Button>

			{filledOrders.map((order, i) => (
				<Accordion
					key={order._id}
					expanded={expanded === `panel${i}`}
					onChange={handleChange(`panel${i}`)}
					sx={{ width: "21rem" }}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls={`panel${i}-content`}
						id={`panel${i}-header`}>
						<Typography component="span" sx={{ width: "33%", flexShrink: 0 }}>
							<span className="font-medium">Crypto:</span>{" "}
							<span className="uppercase tracking-wde">
								{order.cryptoSymbol}
							</span>
						</Typography>
						<Typography
							component="span"
							sx={{
								color: "text.secondary",
								display: "inline-block",
								pl: "1rem",
							}}>
							Trade type:{" "}
							<span
								className={`${
									order.tradeType === "buy" ? "text-green-500" : "text-red-500"
								}`}>
								{order.tradeType}
							</span>
						</Typography>
					</AccordionSummary>
					<AccordionDetails sx={{ textAlign: "left" }}>
						<Typography>
							<span className="font-medium inline-block mr-[0.5rem]">
								Amount traded:
							</span>{" "}
							${order.amount}
						</Typography>
						<Typography>
							<span className="font-medium inline-block mr-[0.5rem]">
								Crypto amount:
							</span>{" "}
							{order.finalCryptoAmount}
						</Typography>
						<Typography>
							<span className="font-medium inline-block mr-[0.5rem]">
								Order status:
							</span>{" "}
							{order.orderStatus}
						</Typography>
						<Typography>Order type: {order.orderType}</Typography>
						<Typography>
							<span className="font-medium inline-block mr-[0.5rem]">
								{order.cryptoSymbol} price:
							</span>{" "}
							${order.price}
						</Typography>
						<Typography>
							<span className="font-medium inline-block mr-[0.5rem]">
								TP/SL:
							</span>{" "}
							${order.takeProfit} <span className="font-bold">/</span> $
							{order.stopLoss}
						</Typography>
						<Typography>
							<span className="font-medium inline-block mr-[0.5rem]">
								Date:
							</span>{" "}
							{order.date}
						</Typography>
						<Button
							onClick={() => {
								deleteSingleOrderHandler(order._id, order.cryptoSymbol);
							}}
							sx={{ bgcolor: "red", color: "white", my: "0.5rem" }}>
							delete <DeleteForeverIcon />
						</Button>
					</AccordionDetails>
				</Accordion>
			))}
		</div>
	);
}
